import { css, Rule } from 'glamor'
import * as React from 'react'
import { defineMessages, FormattedMessage } from 'react-intl'
import * as PropTypes from 'prop-types'

import { UiTheme, useTheme } from '../../theme'

const messages = defineMessages({
  optional: {
    id: 'ui-kit.label.optional',
    defaultMessage: '(Optional)',
  },
})

const getStyles = (theme: UiTheme) => ({
  label: {
    base: {
      display: 'block',
      ...theme.typography.body,
      paddingBottom: theme.settings.spacing.small,
      color: '#666',
    },

    flush: {
      paddingBottom: 0,
    },

    highlight: {
      color: theme.colors.accents.primary,
    },
  },

  optionalLabel: {
    base: {
      color: '#b2b2b2',
    },
  },
})

interface ILabelProps {
  children?: React.ReactNode
  flush?: boolean
  highlight?: boolean
  required?: boolean
  style?: Rule
  htmlFor?: string
}

export const Label: React.FC<ILabelProps> = ({ children, flush, style, highlight, required, ...additional }) => {
  const styles = getStyles(useTheme())
  const labelProps = { ...additional }

  let childrenArr = React.Children.toArray(children)

  // If we only have one child, we'll assume it's a label and apply the
  // styles to the container. Otherwise, we'll wrap any text node in a span
  // and apply the styles to that element instead.
  let computedStyle = style
  if (childrenArr.length === 1) {
    computedStyle = [styles.label.base, flush && styles.label.flush, highlight && styles.label.highlight, style]
  } else {
    childrenArr = childrenArr.map(child =>
      typeof child === 'string' ? (
        <span key="label-text" {...css(styles.label.base)}>
          {child}
        </span>
      ) : (
        child
      )
    )
  }

  /* eslint-disable jsx-a11y/label-has-for */
  return (
    <label {...labelProps} {...css(computedStyle)}>
      {childrenArr}{' '}
      {!required && (
        <span {...css({ ...styles.optionalLabel.base, ...(highlight ? styles.label.highlight : {}) })}>
          <FormattedMessage {...messages.optional} />
        </span>
      )}
    </label>
  )
}

Label.propTypes = {
  children: PropTypes.node,
  flush: PropTypes.bool,
  highlight: PropTypes.bool,
  required: PropTypes.bool,
  style: PropTypes.object,
}
